import { Effect } from 'effect';
import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';

export type StudioToolCategory = 'organize' | 'edit' | 'convert' | 'security' | 'ai' | 'analyze';

export const TOOL_CATEGORY_LABELS: Record<StudioToolCategory, string> = {
  organize: 'Organize',
  edit: 'Edit',
  convert: 'Convert',
  security: 'Security',
  ai: 'AI (Gemini)',
  analyze: 'Analyze',
};

/**
 * A Studio tool plugin. Tools are registered at build time in `registry.ts`;
 * each panel (and with it heavy deps like pdf-lib/pdfjs) is code-split behind
 * `load()` so the shell bundle stays small.
 *
 * Panels receive no props — they consume the workspace via `useStudio()`.
 */
export interface StudioTool {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  category: StudioToolCategory;
  keywords: string[];
  /** Document tools act on the workspace's open PDF; multi-file tools manage their own inputs. */
  requiresDocument: boolean;
  /**
   * The tool calls the compute worker (`/_emdash/api/studio/*`). Hosts without
   * that backend hide it via `hideComputeTools` — categories are labels and do
   * move (#1039 split the compute ops across `ai` + `analyze`, which leaked
   * four broken tools onto okrapdf.com), so the capability is declared per
   * tool instead of being inferred from the category.
   */
  requiresCompute?: boolean;
  /**
   * Keep the tool out of the HomeGrid cards while leaving it fully selectable
   * (rail, `?tool=` deep links, host selection seam). Hosts use this for tools
   * they present in their own home section — e.g. okrapdf.com lists its
   * cloud-backed tools in a dedicated "runs on our servers" section instead of
   * duplicating them in the grid.
   */
  hideFromHomeGrid?: boolean;
  load: () => Promise<{ default: ComponentType }>;
}

export function defineStudioTool(tool: StudioTool): StudioTool {
  return Effect.runSync(defineStudioToolEffect(tool));
}

export function defineStudioToolEffect(tool: StudioTool): Effect.Effect<StudioTool> {
  return Effect.succeed(tool);
}
